import { flow } from "books/reflow";
import { ThemeOptions, Theme } from "names/theme";
import {
  createTheme as createThemeMui,
  responsiveFontSizes,
  teal,
} from "fashion/fabrics";

import "@fontsource/roboto/latin-300.css";
import "@fontsource/roboto/latin-400.css";
import "@fontsource/roboto/latin-500.css";
import "@fontsource/roboto/latin-700.css";
import "@fontsource/dancing-script/latin-400.css";

export const FONT_FAMILY = {
  DEFAULT: "Roboto",
  CURSIVE: "Dancing Script",
};

const createPaletteTheme = (theme: Theme) =>
  createThemeMui(theme, {
    palette: {
      ...theme.palette,
      primary: {
        main: theme.palette.common.black,
        dark: theme.palette.common.black,
        contrastText: "#fff",
      },
      secondary: {
        main: teal[600],
        dark: teal[700],
        light: teal[800],
        contrastText: "#000",
      },
    },
  });

const createComponentsTheme = (theme: Theme) =>
  createThemeMui(theme, {
    components: {
      ...theme.components,
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) =>
            theme.unstable_sx({ displayPrint: "none" }),
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) =>
            theme.unstable_sx({ displayPrint: "none" }),
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            "@media print": {
              backgroundColor: theme.palette.common.white,
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            "& .MuiChip-label": {
              lineHeight: "normal",
            },
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            "@media print": {
              margin: 0,
              padding: 0,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            "@media print": {
              background: "unset",
              boxShadow: "unset",
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) =>
            theme.unstable_sx({ displayPrint: "none" }),
        },
      },
      MuiFab: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) =>
            theme.unstable_sx({ displayPrint: "none" }),
        },
      },
      MuiSpeedDial: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) =>
            theme.unstable_sx({ displayPrint: "none" }),
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            printColorAdjust: "exact",
            colorAdjust: "exact",
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: theme.palette.secondary.main,
            "@media print": {
              textDecoration: "none",
            },
            ":hover": {
              textDecorationColor: theme.palette.secondary.main,
              textDecorationStyle: "dotted",
            },
            ":active": {
              color: theme.palette.secondary.main,
              textDecorationColor: theme.palette.secondary.main,
            },
          },
        },
      },
      MuiTypography: {
        variants: [
          {
            props: { variant: "caption" },
            style: {
              /*
               * Prevent browser font interpolation
               * Default: 0.03em
               */
              letterSpacing: "0em",
            },
          },
        ],
      },
      MuiButtonBase: {
        defaultProps: {
          focusRipple: true,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            /**
             * Mitigate MUI v5 alignement issue
             *   -> Remove & test on MUI v6
             *
             * Issue: https://github.com/mui/material-ui/issues/30529
              "& .MuiButton-iconSizeMedium": { marginTop: -4 },
              "& .MuiButton-iconSizeSmall": {},
              "& .MuiButton-endIcon": {
                marginTop: -6,
                marginLeft: 6,
              },
             */
            "& .MuiButton-iconSizeSmall.MuiButton-startIcon": {
              marginRight: "4px",
            },
            "& .MuiButton-iconSizeSmall.MuiButton-endIcon": {
              marginLeft: "4px",
            },
            /**
             * MUI size override issue
             *
             * Size should not be defined by the parent button component
             */
            "& .MuiButton-endIcon>*:nth-of-type(1)": {
              fontSize: "20px",
            },
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            "&.Mui-selected": { color: theme.palette.secondary.main },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          type: "search", // Prevent text device dumb-completion
        },
      },
      MuiDialog: {
        defaultProps: {
          closeAfterTransition: true,
          keepMounted: false,
        },
      },
      MuiBadge: {
        styleOverrides: {
          root: {
            "& .MuiBadge-anchorOriginBottomRight": {
              bottom: "auto",
            },
            "& .MuiBadge-anchorOriginBottomLeft": {
              bottom: "auto",
            },
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            "&.Mui-active": {
              color: theme.palette.secondary.main,
            },
          },
        },
      },
    },
  });

const createModeTheme = (theme: Theme) =>
  createThemeMui(theme, {
    palette: {
      primary: {
        ...(theme.palette.mode === "dark" && {
          main: "#fff",
          dark: "#fff",
          contrastText: "#000",
        }),
      },
    },
  });

const createBaselineTheme = (theme: Theme) =>
  createThemeMui(theme, {
    components: {
      ...theme.components,
      MuiCssBaseline: {
        styleOverrides: {
          "::selection": {
            color: theme.palette.background.default,
            backgroundColor: theme.palette.secondary.dark,
          },
        },
      },
    },
  });

export const createTheme = (options: ThemeOptions): Theme =>
  flow(
    createThemeMui,
    createBaselineTheme,
    createPaletteTheme,
    createComponentsTheme,
    createModeTheme,
    responsiveFontSizes
  )(options);
